import { HULL_MODEL_IDS } from "./hull-models.js";
import { systemEffectsSchema } from "./ship-systems.js";
import { z } from "zod";
import {
  HULL_IDS,
  MODULE_IDS,
  WEAPON_IDS,
  PASSIVE_IDS,
  NPC_ARCHETYPES,
  ORDINARY_NPC_ARCHETYPES,
  SECTOR_IDS,
} from "./combat-rules-v1-ids.js";

const positive = z.number().positive().finite();
const nonnegative = z.number().nonnegative().finite();
const hullId = z.enum(HULL_IDS);
const weaponId = z.enum(WEAPON_IDS);
const moduleId = z.enum(MODULE_IDS);
const hull = z
  .strictObject({
    id: hullId,
    name: z.string(),
    role: z.string(),
    limitation: z.string(),
    price: z.string().regex(/^\d+$/),
    capacity: z.number().int().positive(),
    hp: positive,
    shield: nonnegative,
    speed: positive,
    turnRate: positive,
    radius: positive,
    frontalDamageFactor: positive,
    boostEnergyFactor: positive,
    repairFactor: positive,
    weaponDamageFactor: positive,
    discoveryRange: nonnegative,
    barrierEnergyCost: nonnegative,
  })
  .readonly();
const weapon = z
  .strictObject({
    id: weaponId,
    damage: positive,
    cooldown: positive,
    range: positive,
    speed: positive,
  })
  .readonly();
const module = z
  .strictObject({ id: moduleId, energy: nonnegative, cooldown: positive })
  .readonly();
const loadout = z
  .strictObject({
    id: z.enum(["assault", "defensive"]),
    hullId,
    weaponId,
    modules: z.tuple([moduleId, moduleId]).readonly(),
  })
  .readonly();
const npc = z
  .strictObject({
    id: z.enum(NPC_ARCHETYPES),
    behavior: z.enum([
      ...ORDINARY_NPC_ARCHETYPES,
      "elite",
      "boss",
      "transport",
    ]),
    name: z.string(),
    hp: positive,
    shield: nonnegative,
    radius: positive,
    speed: nonnegative,
    turnRate: positive,
    preferredRange: nonnegative,
    attackRange: nonnegative,
    charge: nonnegative,
    cooldown: nonnegative,
    weaponId,
    damageMultiplier: nonnegative,
  })
  .readonly();
const passive = z
  .strictObject({
    maxEnergy: positive,
    weaponRangeFactor: positive,
    boostSpeedFactor: positive,
    healingPerSecond: nonnegative,
  })
  .readonly();
const powers = z.tuple([nonnegative, nonnegative, nonnegative]).readonly();
const zone = z
  .strictObject({
    risk: z.enum(["safe-pve", "risky-pve", "open-pvp"]),
    pvpAllowed: z.boolean(),
    ordinaryLossFraction: z.number().min(0).max(1),
    lossDisposition: z.enum(["keep", "destroy", "drop"]),
  })
  .readonly();
const zoneTermsFields = {
  format: z.literal(1),
  balanceId: z.string().min(1).max(100),
  zone,
};
const dropLifetime = z.number().int().positive();
export const zoneLossTermsSchema = z
  .strictObject({ ...zoneTermsFields, dropTtlMs: dropLifetime })
  .readonly();
export type ZoneLossTerms = z.infer<typeof zoneLossTermsSchema>;
export const deathTermsSchema = z
  .strictObject({
    ...zoneTermsFields,
    respawnSeconds: positive,
    dropTtlMs: dropLifetime,
  })
  .readonly();
export type DeathTerms = z.infer<typeof deathTermsSchema>;
export const npcAttackGeometrySchema = z
  .strictObject({
    chargeDistance: nonnegative,
    barrageRadius: nonnegative,
    flakHalfAngle: nonnegative,
    flakRange: nonnegative,
  })
  .readonly();
export type NpcAttackGeometry = z.infer<typeof npcAttackGeometrySchema>;

export const movementRulesSchema = z
  .strictObject({
    energyPerSecond: nonnegative,
    boostEnergyPerSecond: nonnegative,
    boostSpeedFactor: positive,
    forwardBase: nonnegative,
    forwardGain: nonnegative,
    reverseFactor: nonnegative,
  })
  .readonly();
export type MovementRules = z.infer<typeof movementRulesSchema>;
export const flightMovementSchema = z
  .strictObject({
    speed: positive,
    turnRate: positive,
    boostEnergyFactor: positive,
    boostSpeedFactor: positive,
    rules: movementRulesSchema,
  })
  .readonly();
export type FlightMovement = z.infer<typeof flightMovementSchema>;
const flightControlsBaseSchema = z
  .strictObject({
    format: z.literal(1),
    balanceId: z.string().min(1).max(100),
    flightId: z.string().min(1).max(200),
    movement: flightMovementSchema,
    abilityEnergyCosts: z.tuple([nonnegative, nonnegative]).readonly(),
  })
  .readonly();
export const flightControlsSchema = z.union([flightControlsBaseSchema,
  flightControlsBaseSchema.unwrap().extend({ format: z.literal(2), abilityEnergyCosts: z.array(nonnegative).min(2).max(3).readonly(),
    hullTier: z.union([z.literal(1), z.literal(2), z.literal(3)]), thirdModuleId: z.enum(MODULE_IDS).nullable() }).readonly(),
  flightControlsBaseSchema.unwrap().omit({ abilityEnergyCosts: true }).extend({
    format: z.literal(3), hullTier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    abilities: z.array(z.strictObject({ slot: z.number().int().min(0).max(7), id: moduleId,
      tier: z.union([z.literal(0), z.literal(1), z.literal(2)]), energyCost: nonnegative })).max(8).readonly(),
  }).readonly(),
  flightControlsBaseSchema.unwrap().omit({ abilityEnergyCosts: true }).extend({
    format: z.literal(4), hullTier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    abilities: z.array(z.strictObject({ slot: z.number().int().min(0).max(10), id: moduleId,
      tier: z.union([z.literal(0), z.literal(1), z.literal(2)]), energyCost: nonnegative })).max(11).readonly(),
  }).readonly()]);
export type FlightControls = z.infer<typeof flightControlsSchema>;

/** Version 1 stores values, including resolved MK/passive formulas, rather than links to live tables. */
const combatRulesBaseSchema = z
  .strictObject({
    format: z.literal(1),
    balanceId: z.string().min(1).max(100),
    algorithm: z.literal("combat-v1"),
    hulls: z.record(hullId, hull).readonly(),
    weapons: z.record(weaponId, weapon).readonly(),
    modules: z.record(moduleId, module).readonly(),
    loadouts: z.record(z.enum(["assault", "defensive"]), loadout).readonly(),
    npcs: z.record(z.enum(NPC_ARCHETYPES), npc).readonly(),
    npcMechanics: z
      .strictObject({
        chargeSpeed: positive,
        chargeDuration: positive,
        supportRepairRange: positive,
        supportRepairRate: positive,
        disruptorEnergyDrain: nonnegative,
        bossBarrageRadius: positive,
        bossFlakHalfAngle: positive,
        bossFlakRange: positive,
      })
      .readonly(),
    tierFactors: powers,
    modulePowers: z.record(moduleId, powers).readonly(),
    passives: z
      .record(
        z.enum(PASSIVE_IDS),
        z.tuple([passive, passive, passive]).readonly(),
      )
      .readonly(),
    movement: movementRulesSchema,
    combat: z
      .strictObject({
        barrierDamageFactor: positive,
        frontalHalfAngle: positive,
        staleInputSeconds: positive,
        shieldDelay: nonnegative,
        shieldPerSecond: nonnegative,
        stormShieldPerSecond: nonnegative,
        fireHalfAngle: positive,
        flakOffsets: z.array(z.number().finite()).min(1).max(20).readonly(),
        projectileRadius: positive,
        missileRadius: positive,
        missileTurnRate: positive,
        ionShieldFactor: positive,
        ionHullFactor: positive,
        railHits: z.number().int().positive(),
        railDamageRetained: positive,
        mineRadius: positive,
        mineLifetime: positive,
        repairFieldRadius: positive,
        repairFieldLifetime: positive,
        npcMineDamage: nonnegative,
        npcMineRadius: positive,
        npcMineLifetime: positive,
        npcMineOffset: positive,
        ordinaryRespawnSeconds: positive,
        eliteRespawnSeconds: positive,
      })
      .readonly(),
    weather: z
      .record(
        z.enum(SECTOR_IDS),
        z.enum(["clear", "ion-storm", "debris-field"]),
      )
      .readonly(),
    zones: z.record(z.enum(SECTOR_IDS), zone).readonly(),
    respawnSeconds: positive,
    pvpCombatLockSeconds: nonnegative,
    dropTtlMs: z.number().int().positive(),
  })
;
function validateCombatIdentities(rules: { hulls: Readonly<Record<string, { id: string }>>; weapons: Readonly<Record<string, { id: string }>>; modules: Readonly<Record<string, { id: string }>>; npcs: Readonly<Record<string, { id: string }>>; loadouts: Readonly<Record<string, { id: string; modules: readonly string[] }>> }, context: z.RefinementCtx) {
    for (const entries of [
      rules.hulls,
      rules.weapons,
      rules.modules,
      rules.loadouts,
      rules.npcs,
    ])
      for (const [key, value] of Object.entries(entries))
        if (key !== value.id)
          context.addIssue({
            code: "custom",
            message: "Combat definition identity mismatch",
          });
    for (const loadout of Object.values(rules.loadouts))
      if (loadout.modules[0] === loadout.modules[1])
        context.addIssue({
          code: "custom",
          message: "Distinct active modules required",
        });
}
export const combatRulesV1Schema = combatRulesBaseSchema.superRefine(validateCombatIdentities).readonly();
const frameRule = z.strictObject({ weapons: z.number().int().min(1).max(3), active: z.number().int().min(2).max(3),
  passive: z.number().int().min(1).max(2), damage: positive, durability: positive }).readonly();
export const combatRulesV2Schema = combatRulesBaseSchema.extend({
  format: z.literal(2), algorithm: z.literal("combat-v2"),
  frameRules: z.record(z.enum(["1", "2", "3"]), frameRule).readonly(),
  rankRules: z.record(z.enum(["0", "1", "2", "3", "4"]), z.strictObject({
    hp: positive, damage: positive, rewards: positive, xp: z.number().int().positive(),
  }).readonly()).readonly(),
  reactorFactors: z.tuple([positive, positive, positive]).readonly(),
  signatures: z.strictObject({ assaultSixthShot: positive, engineerRepair: positive, scoutRange: positive,
    interceptorSpeed: positive, transportLoadedShield: positive }).readonly(),
}).superRefine(validateCombatIdentities).readonly();
const shipHull = hull.unwrap().extend({ id: z.enum(HULL_MODEL_IDS),
  family: hullId, tier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  weaponSlots: z.number().int().min(1).max(3), equipmentSlots: z.number().int().min(1).max(8),
  componentPrice: z.number().int().nonnegative(), weaponBudget: positive }).readonly();
export const combatRulesV3Schema = combatRulesBaseSchema.extend({
  format: z.literal(3), algorithm: z.literal("combat-v3"),
  hulls: z.record(z.enum(HULL_MODEL_IDS), shipHull).readonly(),
  rankRules: combatRulesV2Schema.unwrap().shape.rankRules,
  signatures: combatRulesV2Schema.unwrap().shape.signatures,
  systems: systemEffectsSchema,
  weaponBudget: z.strictObject({ damagePerSecond: positive, energyPerSecond: positive, volleySeconds: positive }).readonly(),
}).superRefine(validateCombatIdentities).readonly();
const commonCellHull = hull.unwrap().extend({ id: z.enum(HULL_MODEL_IDS),
  family: hullId, tier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  equipmentSlots: z.number().int().min(1).max(11), componentPrice: z.number().int().nonnegative(), weaponBudget: positive }).readonly();
export const combatRulesV4Schema = combatRulesBaseSchema.extend({
  format: z.literal(4), algorithm: z.literal("combat-v4"),
  hulls: z.record(z.enum(HULL_MODEL_IDS), commonCellHull).readonly(),
  rankRules: combatRulesV2Schema.unwrap().shape.rankRules,
  signatures: combatRulesV2Schema.unwrap().shape.signatures,
  systems: systemEffectsSchema,
  weaponBudget: z.strictObject({ damagePerSecond: positive, energyPerSecond: positive, volleySeconds: positive,
    additionalDamageFactor: nonnegative, additionalEnergyFactor: nonnegative }).readonly(),
}).superRefine(validateCombatIdentities).readonly();
export const combatRulesSchema = z.union([combatRulesV1Schema, combatRulesV2Schema, combatRulesV3Schema, combatRulesV4Schema]);
export type CombatRules = z.infer<typeof combatRulesSchema>;
export type ShipCombatRules = Extract<CombatRules, { format: 3 | 4 }>;
export function isShipCombatRules(rules: CombatRules): rules is ShipCombatRules {
  return rules.format === 3 || rules.format === 4;
}
